package top.wcpe.allincore.ui.web.model

import kotlin.js.Promise

internal data class ThreeStaticModelRequest(
    val modelBase: String,
    val source: String,
    val texture: String?,
)

internal fun interface ThreeStaticModelPreparer {
    fun prepareStaticModels(requests: List<ThreeStaticModelRequest>): Promise<ThreePreparedModelSet>
}

internal class ThreePreparedModelSet(
    private val entries: List<ThreePreparedModelEntry>,
    private val activateAction: (ThreePreparedModelSet) -> ThreePreparedModelSet?,
    private val replaceAction: (ThreePreparedModelSet, ThreePreparedModelSet?) -> Unit,
) {
    private var previous: ThreePreparedModelSet? = null
    private var activated = false
    private var confirmed = false
    private var disposed = false

    val size: Int
        get() = entries.size

    fun activate() {
        check(!disposed && !activated) { "静态模型预备集合不可重复激活" }
        previous = activateAction(this)
        activated = true
    }

    fun confirmActivation() {
        check(activated && !disposed) { "静态模型预备集合尚未激活" }
        confirmed = true
        previous = null
    }

    fun entry(request: ThreeStaticModelRequest): ThreePreparedModelEntry? =
        if (disposed) null else entries.firstOrNull { it.request == request }

    fun owns(bundle: ThreeSceneBundle): Boolean = !disposed && entries.any { it.bundle === bundle }

    fun dispose() {
        if (disposed) return
        disposed = true
        if (activated) replaceAction(this, previous.takeUnless { confirmed })
        entries.asReversed().forEach { it.bundle.dispose() }
    }
}

internal data class ThreePreparedModelEntry(
    val request: ThreeStaticModelRequest,
    val key: ThreeModelBundleKey,
    val module: dynamic,
    val bundle: ThreeSceneBundle,
)

internal class ThreePreparedModelBuilder(
    private val module: dynamic,
    private val assetLoader: ThreeModelAssetLoader,
    private val activateAction: (ThreePreparedModelSet) -> ThreePreparedModelSet?,
    private val replaceAction: (ThreePreparedModelSet, ThreePreparedModelSet?) -> Unit,
) {
    private val entries = mutableListOf<ThreePreparedModelEntry>()

    fun prepare(requests: List<ThreeStaticModelRequest>): Promise<ThreePreparedModelSet> {
        val groups = requests.distinct().groupBy { SourceKey(it.modelBase, it.source) }.entries.toList()
        return prepareGroups(groups, 0).then {
            ThreePreparedModelSet(entries.toList(), activateAction, replaceAction)
        }.catch { reason ->
            entries.asReversed().forEach { it.bundle.dispose() }
            entries.clear()
            throw asThrowable(reason)
        }
    }

    private fun prepareGroups(groups: List<Map.Entry<SourceKey, List<ThreeStaticModelRequest>>>, index: Int): Promise<Unit> {
        if (index >= groups.size) return Promise.resolve(Unit)
        val group = groups[index]
        return assetLoader.loadModel(group.key.modelBase, group.key.source).flatMap { asset ->
            prepareVariants(asset, group.value, 0)
        }.flatMap { prepareGroups(groups, index + 1) }
    }

    private fun prepareVariants(
        asset: ThreeModelAsset,
        requests: List<ThreeStaticModelRequest>,
        index: Int,
    ): Promise<Unit> {
        if (index >= requests.size) return Promise.resolve(Unit)
        val request = requests[index]
        val model = parse(asset, request)
        return ThreeSceneBuilder(module, assetLoader).build(asset, model).flatMap { bundle ->
            entries += ThreePreparedModelEntry(request, request.bundleKey(asset), module, bundle)
            prepareVariants(asset, requests, index + 1)
        }
    }

    private fun parse(asset: ThreeModelAsset, request: ThreeStaticModelRequest): RestrictedBbModel {
        val override = request.texture?.let { RestrictedBbTextureOverride(request.modelBase, it) }
        return RestrictedBbModelParser.parse(asset.text, asset.canonicalPath, override)
    }

    private data class SourceKey(val modelBase: String, val source: String)
}

private fun ThreeStaticModelRequest.bundleKey(asset: ThreeModelAsset): ThreeModelBundleKey = ThreeModelBundleKey(
    source,
    texture,
    modelBase,
    asset.canonicalPath,
    asset.contentVersion,
)

private fun asThrowable(reason: dynamic): Throwable = reason as? Throwable ?: RuntimeException(reason?.toString() ?: "未知错误")

private fun <T, R> Promise<T>.flatMap(transform: (T) -> Promise<R>): Promise<R> =
    then(transform).unsafeCast<Promise<R>>()
